import { useIntl } from 'react-intl';
import type { LandIqDataRunCommandProgress, LandIqDataRunEstimate, LandIqDataRunMetrics } from '@immo/api-contract';
import { ClockIcon } from '@/components/layout/icons';
import { useTranslations } from '@/i18n/use-translations';
import { PlannedEstimate, RunEstimate } from '@/components/landiq/run-estimate';
import { RunProgressBar } from '@/components/landiq/run-progress-bar';
import { formatDuration } from '@/lib/landiq/format-duration';
import { isProgressStale, knownStageTimes, progressPercent, progressShare } from '@/lib/landiq/run-metrics';

type Props = {
  /** The open entry's latest run metrics; null when LandIQ has none. */
  readonly metrics: LandIqDataRunMetrics | null;
  /** The board's own snapshot of the open entry's last run; read before `metrics`, which a failed run-list read leaves out. */
  readonly lastRunProgress: LandIqDataRunCommandProgress | null;
  /** Whether the run's entry is running: progress is shown only then, and only then can it go stale. */
  readonly running: boolean;
  /** The open entry's manifest estimate, and how its latest run compares with it; null when LandIQ sends none. */
  readonly estimateMinutes: number | null;
  readonly estimate: LandIqDataRunEstimate | null;
};

/**
 * The card's run metrics, under its state: progress while it runs, an import's chunk counts, the known stage
 * times, the run against its estimate. What LandIQ does not report is left out, never guessed; with nothing known
 * the block is not drawn.
 */
export function CardRunMetrics({ metrics, lastRunProgress, running, estimateMinutes, estimate }: Props) {
  const t = useTranslations('pages.admin.landiq-data-run.metrics');
  const intl = useIntl();
  const progress = running ? (lastRunProgress ?? metrics?.commandProgress ?? null) : null;
  const chunkStats = metrics?.chunkStats ?? null;
  const times = metrics?.timings ? knownStageTimes(metrics.timings) : [];

  if (progress === null && chunkStats === null && times.length === 0 && estimate === null && estimateMinutes === null) {
    return null;
  }

  const number = (value: number) => intl.formatNumber(value);
  const at = (iso: string) =>
    intl.formatDate(iso, {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Berlin',
    });
  const share = progress === null ? null : progressShare(progress);
  const percent = progress === null ? null : progressPercent(progress);
  const stale = progress !== null && isProgressStale(progress, running, Date.now());

  return (
    <div className="flex flex-col gap-1.5 border-t border-border pt-2 text-xs tabular-nums text-text-secondary">
      {progress !== null && (
        <>
          <p className="truncate font-mono text-text-tertiary" title={progress.phase}>
            {progress.phase}
          </p>
          {share !== null && (
            <RunProgressBar share={share} stale={stale} label={t('progress-label', { phase: progress.phase })} />
          )}
          <p>
            {[
              percent === null ? null : t('percent', { percent }),
              progress.total === null
                ? t('done-only', { done: number(progress.done) })
                : t('done-ratio', { done: number(progress.done), total: number(progress.total) }),
              progress.itemsPerMinute === null
                ? null
                : t('rate', { rate: intl.formatNumber(progress.itemsPerMinute, { maximumFractionDigits: 1 }) }),
              progress.etaAt === null ? null : t('eta', { time: at(progress.etaAt) }),
            ]
              .filter((part) => part !== null)
              .join(' · ')}
          </p>
          {stale && (
            <p className="flex items-center gap-1 font-medium text-warning-700">
              <ClockIcon aria-hidden className="h-3 w-3" />
              {t('stale', { age: formatDuration(Date.now() - Date.parse(progress.updatedAt)) })}
            </p>
          )}
        </>
      )}
      {chunkStats !== null && (
        <p>
          {intl.formatMessage(
            { id: 'pages.admin.landiq-data-run.metrics.card-chunks' },
            {
              imported: number(chunkStats.imported),
              failed: (
                <span key="failed" className={chunkStats.failed > 0 ? 'text-danger-700' : undefined}>
                  {number(chunkStats.failed)}
                </span>
              ),
            },
          )}
        </p>
      )}
      {times.length > 0 && (
        <p className="text-text-tertiary">
          {times
            .map(({ stage, ms }) => t('stage-time', { stage: t(`stages.${stage}`), duration: formatDuration(ms) }))
            .join(' · ')}
        </p>
      )}
      {estimate !== null && (
        <p>
          <RunEstimate estimate={estimate} running={running} />
        </p>
      )}
      {estimate === null && estimateMinutes !== null && (
        <p className="tabular-nums text-text-tertiary">
          <PlannedEstimate minutes={estimateMinutes} />
        </p>
      )}
    </div>
  );
}
